import { message } from 'antd';
import { useEffect, useState } from 'react';

import { useSocketContext } from '@/libs/hooks/useSocketContext';
import { TransitEvent } from '@/pages/transit-with-partner/coordination2/enums/transit.event.enum';
import { transitNotificationsListWithPaging } from '@/services/api-transit/transitNotifications';
import classNames from 'classnames';
import { introductionsTarget } from '../Introduction';
import styles from './index.less';
import NoticeTransitIcon from './NoticeTransitIcon';

export type GlobalHeaderRightProps = {
  fetchingNotices?: boolean;
  onNoticeVisibleChange?: (visible: boolean) => void;
  onNoticeClear?: (tabName?: string) => void;
};

const NoticeIconView = () => {
  // Separate state for each tab
  const [notificationNotices, setNotificationNotices] = useState<
    API.NoticeIconItem[]
  >([]);
  const [messageNotices, setMessageNotices] = useState<API.NoticeIconItem[]>(
    [],
  );
  const [eventNotices, setEventNotices] = useState<API.NoticeIconItem[]>([]);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;
  const [hasMore, setHasMore] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);

  // Separate counts for each tab
  const [messageCount, setMessageCount] = useState(0);
  const [eventCount, setEventCount] = useState(0);

  const { on, off } = useSocketContext();

  const [activeTab, setActiveTab] = useState<
    'notification' | 'message' | 'event'
  >('notification');

  const getFilterForTab = (tabKey: 'notification' | 'message' | 'event') => {
    if (tabKey === 'message') return { type: 'info' };
    if (tabKey === 'event') return { type: 'alert' };
    return {};
  };

  const mapNotificationToNoticeItem = (
    item: API.TransitNotification,
  ): API.NoticeIconItem => ({
    id: item.id,
    avatar: undefined,
    title: item.title,
    datetime: item.createdAt,
    type: item.type === 'alert' ? 'event' : 'message',
    read: item.status === 'read',
    description: item.content,
    extra: item.priority === 'high' ? 'Ưu tiên cao' : undefined,
    status: item.priority === 'high' ? 'urgent' : 'processing',
  });

  const fetchNotifications = async (
    pageToLoad: number,
    mode: 'replace' | 'append' = 'replace',
    tabKey: 'notification' | 'message' | 'event' = activeTab,
  ) => {
    const response: any = await transitNotificationsListWithPaging({
      current: pageToLoad,
      pageSize: limit,
      filter: JSON.stringify(getFilterForTab(tabKey)),
      sort: 'createdAt.desc',
    });
    const data = response?.data?.data || response?.data || [];
    const mapped: API.NoticeIconItem[] = data.map(mapNotificationToNoticeItem);

    // Update state for the specific tab
    if (tabKey === 'notification') {
      setNotificationNotices((prev) =>
        mode === 'append' ? [...prev, ...mapped] : mapped,
      );
    } else if (tabKey === 'message') {
      setMessageNotices((prev) =>
        mode === 'append' ? [...prev, ...mapped] : mapped,
      );
      // Update message count
      const unreadCount = mapped.filter((item) => !item.read).length;
      if (mode === 'replace') {
        setMessageCount(unreadCount);
      }
    } else if (tabKey === 'event') {
      setEventNotices((prev) =>
        mode === 'append' ? [...prev, ...mapped] : mapped,
      );
      // Update event count
      const unreadCount = mapped.filter((item) => !item.read).length;
      if (mode === 'replace') {
        setEventCount(unreadCount);
      }
    }

    setPage(pageToLoad);
    setHasMore(data.length !== total);
    setTotal(response?.data?.total || 0);
  };

  // Load initial data from API for all tabs
  useEffect(() => {
    const loadNotifications = async () => {
      setLoading(true);
      try {
        // Load all three tabs
        await Promise.all([
          fetchNotifications(1, 'replace', 'notification'),
          fetchNotifications(1, 'replace', 'message'),
          fetchNotifications(1, 'replace', 'event'),
        ]);
      } catch (error) {
        console.error('Error loading notifications:', error);
        message.error('Không thể tải thông báo');
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, []); // Only load once on mount

  const loadMore = async (tabKey?: 'notification' | 'message' | 'event') => {
    if (fetchingMore || !hasMore) return;
    setFetchingMore(true);
    try {
      const nextPage = page + 1;
      await fetchNotifications(nextPage, 'append', tabKey ?? activeTab);
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setFetchingMore(false);
    }
  };

  const handleNewTransitNotification = async (notification?: any) => {
    try {
      // Determine which tab to update based on notification type
      if (notification?.type === 'info') {
        // Update message tab
        await fetchNotifications(1, 'replace', 'message');
      } else if (notification?.type === 'alert') {
        // Update event tab
        await fetchNotifications(1, 'replace', 'event');
      } else {
        // Update all tabs if type is unknown
        await Promise.all([
          fetchNotifications(1, 'replace', 'message'),
          fetchNotifications(1, 'replace', 'event'),
        ]);
      }

      // Always update the notification tab (all)
      await fetchNotifications(1, 'replace', 'notification');
    } catch (error) {
      console.error('Error refreshing notifications from socket:', error);
    }
  };

  // Handle WebSocket event for new transit notifications
  useEffect(() => {
    const handleSocketEvent = (notification?: any) => {
      handleNewTransitNotification(notification);
    };

    on(TransitEvent.NEW_TRANSIT_NOTIFICATION, handleSocketEvent);

    return () => {
      off(TransitEvent.NEW_TRANSIT_NOTIFICATION, handleSocketEvent);
    };
  }, [on, off]);

  // Combine all notifications for the 'notification' tab
  const allNotices = [...messageNotices, ...eventNotices];

  const noticeData = {
    notification: notificationNotices,
    message: messageNotices,
    event: eventNotices,
  };

  const changeReadState = (id: string) => {
    // Update read state in all relevant tabs
    setNotificationNotices(
      notificationNotices.map((item) => {
        const notice = { ...item };
        if (notice.id === id) {
          notice.read = true;
        }
        return notice;
      }),
    );

    setMessageNotices(
      messageNotices.map((item) => {
        const notice = { ...item };
        if (notice.id === id) {
          notice.read = true;
          setMessageCount((prev) => Math.max(0, prev - 1));
        }
        return notice;
      }),
    );

    setEventNotices(
      eventNotices.map((item) => {
        const notice = { ...item };
        if (notice.id === id) {
          notice.read = true;
          setEventCount((prev) => Math.max(0, prev - 1));
        }
        return notice;
      }),
    );
  };

  const clearReadState = (title: string, key: string) => {
    if (key === 'notification') {
      setNotificationNotices(
        notificationNotices.map((item) => ({ ...item, read: true })),
      );
    } else if (key === 'message') {
      setMessageNotices(
        messageNotices.map((item) => ({ ...item, read: true })),
      );
      setMessageCount(0);
    } else if (key === 'event') {
      setEventNotices(eventNotices.map((item) => ({ ...item, read: true })));
      setEventCount(0);
    }
    message.success(`Emptied ${title}`);
  };

  return (
    <NoticeTransitIcon
      className={classNames(
        introductionsTarget.dashboard.noticeButton.substr(1),
        styles.action,
      )}
      count={messageCount + eventCount}
      onItemClick={(item) => {
        changeReadState(item.id!);
      }}
      onClear={(title: string, key: string) => clearReadState(title, key)}
      loading={loading}
      clearText="Xóa"
      viewMoreText="Xem thêm"
      onViewMore={(tabProps) => loadMore(tabProps.tabKey)}
      onTabChange={(key) => setActiveTab(key as any)}
    >
      <NoticeTransitIcon.Tab
        tabKey="notification"
        count={messageCount + eventCount}
        list={allNotices}
        title="Tất cả"
        emptyText="Đã tải hết thông báo"
        showViewMore={allNotices.length < messageCount + eventCount}
        showClear={false}
      />
      <NoticeTransitIcon.Tab
        tabKey="message"
        count={messageCount}
        list={noticeData.message}
        title="Đơn hàng mới"
        emptyText="Không có đơn hàng mới"
        showViewMore={noticeData.message.length < messageCount}
        showClear={false}
      />
      <NoticeTransitIcon.Tab
        tabKey="event"
        count={eventCount}
        list={noticeData.event}
        title="Sự cố nghiêm trọng"
        emptyText="Không có sự cố"
        showViewMore={noticeData.event.length < eventCount}
        showClear={false}
      />
    </NoticeTransitIcon>
  );
};

export default NoticeIconView;
